import React, { useState } from 'react';
import axios from 'axios';
import { Phone, MapPin, Clock, Send, CheckCircle2 } from 'lucide-react';
import PageShell from '@/components/PageShell';
import SEO from '@/components/SEO';
import { useLanguage } from '@/i18n/LanguageContext';
import { track, buildLeadAttribution } from '@/lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { lang, t } = useLanguage();
  const [form, setForm] = useState({ name: '', phone: '', message: '', consent: false, website: '' });
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { setErr(lang === 'es' ? 'Se requiere consentimiento' : 'Consent is required'); return; }
    setBusy(true); setErr('');
    try {
      const attribution = buildLeadAttribution();
      await axios.post(`${API}/leads`, {
        requester_name: form.name, callback_phone: form.phone, jail_city: 'Not sure / Other',
        defendant_name: form.name, notes: form.message, consent_call_text: form.consent,
        preferred_language: lang, website: form.website, ...attribution,
      });
      track('form_submit', { source: 'contact_page' });
      setSent(true);
    } catch (e2) {
      setErr(lang === 'es' ? 'Algo salio mal. Por favor llame directamente.' : 'Something went wrong. Please call us directly.');
    } finally { setBusy(false); }
  };

  return (
    <PageShell trackPage="/contact">
      <SEO title="Contact Amigo's Bail Bonds | Conroe, TX" description="Contact Amigo's Bail Bonds for 24/7 bail bond help in Montgomery County, Texas." path="/contact" />
      <div className="page-hero">
        <div className="eyebrow">{t('nav.contact')}</div>
        <h1 className="section-title">{lang === 'es' ? 'CONTACTENOS' : 'GET IN TOUCH'}</h1>
      </div>
      <section className="section section--void">
        <div className="container">
          <div className="card-grid card-grid--2" style={{ gap: '2rem' }}>
            <div>
              <div className="info-card mb-4"><Phone size={18} color="var(--gold)" style={{ marginBottom: '0.5rem' }} /><div style={{ color: 'var(--text-primary)', fontWeight: 700 }}><a href="tel:9368282500" style={{ color: 'inherit' }} onClick={() => track('phone_click', { location: 'contact_page' })}>(936) 828-2500</a></div></div>
              <div className="info-card mb-4"><MapPin size={18} color="var(--gold)" style={{ marginBottom: '0.5rem' }} /><div style={{ color: 'var(--text-primary)' }}>Conroe, TX 77301 - Montgomery County</div></div>
              <div className="info-card mb-4"><Clock size={18} color="var(--gold)" style={{ marginBottom: '0.5rem' }} /><div style={{ color: 'var(--text-primary)' }}>{lang === 'es' ? '24/7 - Todos los dias' : '24/7 - Every day, including holidays'}</div></div>
              <div style={{ borderRadius: 4, overflow: 'hidden', border: '1px solid var(--border-dim)' }}>
                <iframe title="Conroe, TX Map" width="100%" height="220" style={{ border: 0, filter: 'grayscale(0.3) invert(0.92)' }} loading="lazy" src="https://www.google.com/maps?q=Conroe,TX+77301&output=embed" />
              </div>
            </div>
            <div className="wizard-shell" style={{ maxWidth: 'none' }}>
              {sent ? (
                <div className="text-center" data-testid="contact-success"><CheckCircle2 size={40} color="var(--success)" style={{ marginBottom: '0.8rem' }} /><p style={{ color: 'var(--text-primary)' }}>{lang === 'es' ? 'Gracias! Nos pondremos en contacto pronto.' : "Thank you! We'll be in touch shortly."}</p></div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form">
                  <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} tabIndex="-1" autoComplete="off" aria-hidden="true" className="sr-only" />
                  <div className="form-field">
                    <label className="form-label">{lang === 'es' ? 'Nombre' : 'Name'}<span className="req">*</span></label>
                    <input required className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" />
                  </div>
                  <div className="form-field">
                    <label className="form-label">{lang === 'es' ? 'Telefono' : 'Phone'}<span className="req">*</span></label>
                    <input required type="tel" className="form-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone-input" />
                  </div>
                  <div className="form-field">
                    <label className="form-label">{lang === 'es' ? 'Mensaje' : 'Message'}</label>
                    <textarea className="form-textarea" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" />
                  </div>
                  <div className="consent-box">
                    <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} data-testid="contact-consent-checkbox" />
                    <p>{lang === 'es' ? "Acepto que Amigo's Bail Bonds puede contactarme por telefono o mensaje de texto." : "I agree that Amigo's Bail Bonds may contact me by phone or text."}</p>
                  </div>
                  {err && <div className="form-error mb-4">{err}</div>}
                  <button type="submit" className="btn-red" disabled={busy} data-testid="contact-submit-btn" style={{ width: '100%', justifyContent: 'center' }}><Send size={16} /> {busy ? t('common.loading') : t('common.submit')}</button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default Contact;
